import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allArticles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'story');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const filteredArticles = allArticles.filter((a) => a.categorySlug === activeSlug);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-400 mb-8 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">{activeCategory.label}</span>
            </nav>

            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              {activeCategory.label}
            </h1>
            <p className="text-foreground-500 mb-10">{activeCategory.description}</p>

            <div className="flex flex-wrap gap-3 mb-12">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center inline-block mr-1.5">
                    <i className={cat.icon}></i>
                  </span>
                  {cat.label}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-2 cursor-pointer"
                >
                  <div className="relative aspect-[16/9] overflow-hidden">
                    <img
                      src={article.image}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute top-3 left-3 px-3 py-1 text-xs font-medium rounded-full bg-primary-500/90 text-background-50 whitespace-nowrap">
                      {article.category}
                    </span>
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 text-xs text-foreground-400 mb-3">
                      <span>{article.date}</span>
                      <span>·</span>
                      <span>{article.readTime}阅读</span>
                    </div>
                    <h2 className="text-base font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                      {article.title}
                    </h2>
                    <p className="text-foreground-500 text-sm leading-relaxed line-clamp-2">
                      {article.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>

            {filteredArticles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-300">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-400">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://6shouyou.cn/' },
              { '@type': 'ListItem', position: 2, name: activeCategory.label, item: `https://6shouyou.cn/category/${activeSlug}` },
            ],
          }),
        }}
      />
    </>
  );
}